import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, setToken, clearToken, getToken, errMsg } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authOpen, setAuthOpen] = useState(false);
    const [authMode, setAuthMode] = useState("signup"); // 'signup' | 'login'
    const [afterLogin, setAfterLogin] = useState(null); // callback after successful auth

    const [config, setConfig] = useState({
        razorpay_key_id: "",
        razorpay_ready: false,
        business_whatsapp: "919000000000",
    });

    const refresh = useCallback(async () => {
        if (!getToken()) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            clearToken();
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/config");
                setConfig(data);
            } catch {
                /* ignore */
            }
        })();
        refresh();
    }, [refresh]);

    const signup = useCallback(async ({ name, phone, email, password }) => {
        const { data } = await api.post("/auth/signup", { name, phone, email, password });
        setToken(data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const login = useCallback(async ({ phone, password }) => {
        const { data } = await api.post("/auth/login", { phone, password });
        setToken(data.token);
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(() => {
        clearToken();
        setUser(null);
    }, []);

    const requireAuth = useCallback((cb, mode = "signup") => {
        if (user) {
            cb?.();
        } else {
            setAuthMode(mode);
            setAfterLogin(() => cb);
            setAuthOpen(true);
        }
    }, [user]);

    const onAuthSuccess = useCallback(() => {
        setAuthOpen(false);
        const cb = afterLogin;
        setAfterLogin(null);
        setTimeout(() => cb?.(), 60);
    }, [afterLogin]);

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                config,
                signup,
                login,
                logout,
                refresh,
                authOpen,
                setAuthOpen,
                authMode,
                setAuthMode,
                requireAuth,
                onAuthSuccess,
                errMsg,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be inside AuthProvider");
    return ctx;
};
